import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useVersion } from '../../features/common/hooks.js';
import { useHomePopup } from '../../features/common/home-popup-context.js';
import { HomeContentCard } from '../../features/common/pages/HomePage.js';
import { useAuth } from '../../lib/auth-context.js';
import { NavSidebar } from './NavSidebar.js';

/**
 * Iframe-aware layout for the customer SPA. Mirrors the legacy
 * `wf-audit` layout shell — fixed-width branded sidebar on the left,
 * full-bleed content on the right. The legacy app had no separate
 * top bar (the brand mark lives in the sidebar); we add a slim
 * signed-in pill so the operator can sign out from any page.
 */
export function IframeShell(): JSX.Element {
  const { session, signOut } = useAuth();
  const { data: version } = useVersion();
  const isMac = useIsMac();
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  return (
    // `w-screen overflow-x-hidden` clamps the shell to the viewport.
    // Without it, the inner content's intrinsic width (wide tables,
    // long headers) drives the flex parent wider than 100vw and the
    // top-bar's right-aligned controls (sign-out, search pill) slide
    // off-screen. Operator-reported: "layout shifted to the right
    // again, cuts action more button, sign out button etc."
    <div className="relative z-10 flex min-h-screen w-screen overflow-x-hidden text-foreground">
      {session ? <NavSidebar /> : null}

      {/* Constant `pl-[64px]` matches the collapsed sidebar width. The
          sidebar is `fixed`-positioned (overlays content when expanded),
          so the main content area stays put regardless of sidebar state.
          `min-w-0` is the standard flex trick that lets the column
          shrink (and its long-content children scroll horizontally
          internally) instead of pushing the parent wider. */}
      <div className={`flex min-w-0 grow flex-col ${session ? 'pl-[64px]' : ''}`}>
        {/* Slim top bar — palette hint pill + signed-in identity + sign-out. */}
        {session ? (
          <header className="flex items-center justify-end gap-3 px-4 py-2 text-xs">
            <button
              type="button"
              onClick={() => {
                // Re-fire the same keyboard event the palette listens
                // for. Avoids prop-drilling an open setter through the
                // shell — the palette already owns its state.
                window.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    key: 'k',
                    ctrlKey: !isMac,
                    metaKey: isMac,
                  }),
                );
              }}
              className="inline-flex items-center gap-2 rounded-md border border-foreground/15 bg-foreground/[0.04] px-2 py-1 text-foreground/60 transition-colors hover:border-foreground/40 hover:text-foreground/90"
              title="Open command palette"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Search…</span>
              <kbd className="ml-1 rounded border border-foreground/15 bg-foreground/10 px-1.5 py-0 font-mono text-[10px] text-foreground/70">
                {cmdKey}K
              </kbd>
            </button>
            <span className="text-foreground/80">{session.email ?? session.workfrontUserId}</span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-md border border-foreground/20 bg-foreground/5 px-2.5 py-1 text-foreground/90 transition-colors hover:border-foreground/60 hover:bg-foreground/10"
            >
              Sign out
            </button>
          </header>
        ) : null}

        <main className="flex grow flex-col">
          <Outlet />
        </main>

        <footer className="px-4 py-1 text-right text-[11px] text-foreground/40">
          <span className="font-mono">
            WorkFocus FCOE · {version?.env ?? '…'}
            {version?.commitSha && version.commitSha !== 'unknown'
              ? ` · ${version.commitSha.slice(0, 7)}`
              : ''}
          </span>
        </footer>
      </div>

      {/* Home-popup overlay — raised by the sidebar's WorkFocus logo
          click (NavSidebar → `useHomePopup().open()`). Sits at the
          IframeShell level so it floats over both the sidebar and
          the current slice route. Body content is the same
          `<HomeContentCard/>` the chromed `/` Home route renders, so
          the two surfaces never diverge. */}
      <HomePopupOverlay />
    </div>
  );
}

/**
 * Full-viewport overlay that re-renders the home hero card on top of
 * whatever page the operator is currently viewing. Driven by
 * `useHomePopup`. Picking a product card inside the overlay closes
 * it via the `onPick` callback so navigation lands cleanly on the
 * chosen route.
 */
function HomePopupOverlay(): JSX.Element | null {
  const { isOpen, close } = useHomePopup();

  // Close on Escape — matches the Dialog primitive's usual UX but
  // we hand-roll the overlay so the bokeh background fills the
  // whole viewport (Radix's DialogContent constrains via flex).
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="WorkFocus Center of Excellence — Home"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => {
        // Click on the backdrop (but not on the card itself) closes.
        if (e.target === e.currentTarget) close();
      }}
      style={{
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className="relative w-full max-w-[936px]">
        <button
          type="button"
          onClick={close}
          aria-label="Close home overlay"
          title="Close"
          className="absolute right-3 top-3 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-white/30 bg-black/40 text-white transition-colors hover:bg-black/60"
        >
          <X className="h-4 w-4" />
        </button>
        <HomeContentCard variant="popup" onPick={close} />
      </div>
    </div>
  );
}

/**
 * Detect macOS vs everywhere-else for the Cmd/Ctrl keycap hint. We
 * intentionally avoid `navigator.userAgent` — it's deprecated and
 * unreliable. `navigator.platform` is still good enough for a UI
 * hint (matches the OS the keyboard lives on); SSR-safe via the
 * window guard.
 */
function useIsMac(): boolean {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const platform = (navigator.platform ?? '').toLowerCase();
    setIsMac(platform.includes('mac'));
  }, []);
  return isMac;
}
